import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OrderViewModel } from '@order/models/order-view-model';
import { QuickOrderItem } from '@order/models/quick-order-entry-view-model';
import { OrderService } from '@order/services/order.service';
import { Product } from '@shared/models/product.model';
import { customerProductNumberSelector } from '@shared/selectors/quick-order-entry.selectors';
import { ProductService } from '@shared/services/product/product.service';
import { ListItemState } from '@usf/ngrx-list';
import { orderItemSelectors, OrderStatus } from '@usf/ngrx-order';
import { LoadingState } from '@usf/ngrx-product';
import {
  Observable,
  combineLatest,
  filter,
  map,
  take,
  lastValueFrom,
  delay,
  switchMap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class QuickOrderEntryService {
  constructor(
    private store: Store,
    private productService: ProductService,
    private orderService: OrderService,
  ) {}

  getCurrentOrder$(): Observable<OrderViewModel> {
    return this.orderService.loadOrderViewModel$().pipe(filter(vm => !!vm));
  }

  getProductsForQuickOrder$(
    productNumbers: number[],
  ): Observable<Map<number, Product>> {
    // Use reactive loadProducts$ instead of imperative loadProducts() to prevent race conditions when dealing with large numbers of products
    return this.productService
      .loadProducts$(productNumbers)
      .pipe(switchMap(() => this.productService.getProducts(productNumbers)));
  }

  getListItem$(
    customerProductNumber: string,
  ): Observable<Map<string, ListItemState>> {
    return this.store.select(
      customerProductNumberSelector(customerProductNumber),
    );
  }

  async qoeProductSearch(
    productNumber: number,
    delayTime: number = 0,
  ): Promise<QuickOrderItem> {
    const qoeSearch = combineLatest([
      this.getProductsForQuickOrder$([productNumber]),
      this.productService.selectLoadingStateOfProducts([productNumber]).pipe(
        delay(delayTime),
        filter(
          loadingState =>
            loadingState === LoadingState.loaded ||
            loadingState === LoadingState.error,
        ),
      ),
      this.store.select(orderItemSelectors.selectOrderItemState),
      this.orderService.isPricingForProductsReady$(),
    ]).pipe(
      filter(
        ([productMap, loadingState, orderItemState, pricingReady]) =>
          delayTime <= 0 || (!!productMap && pricingReady),
      ),
      take(1),
      map(([productMap, loadingState, orderItemState, pricingReady]) => {
        let qoeProd: QuickOrderItem = {
          productNumber,
          loadingError: loadingState === LoadingState.error,
          product: productMap.get(productNumber),
          orderItemInCart: orderItemState?.entities[productNumber],
        };
        return qoeProd;
      }),
    );
    return await lastValueFrom(qoeSearch);
  }

  async qoeCustomerProductNumberSearch(
    customerProductNumber: string,
  ): Promise<number> {
    const qoeCpnSearch = this.getListItem$(customerProductNumber).pipe(
      filter(state => !!state),
      take(1),
      map(listItemState => {
        let lowestItemSequence = Infinity;
        let associatedProductNumber: number;

        for (let [productId, listItem] of listItemState) {
          if (listItemState?.size > 1) {
            if (listItem?.itemSequenceNumber < lowestItemSequence) {
              associatedProductNumber = listItem?.productNumber;
              lowestItemSequence = listItem?.itemSequenceNumber;
            }
          } else {
            associatedProductNumber = listItem?.productNumber;
          }
        }

        return associatedProductNumber ?? null;
      }),
    );
    return await lastValueFrom(qoeCpnSearch);
  }
}
